import {
  ShaderMaterial,
  Matrix4,
  Vector2,
  Vector3,
  Vector4,
  Texture,
  GLSL3,
  IUniform,
  type PerspectiveCamera,
} from 'three';

import fullscreenVert from '../shaders/fullscreen.vert?raw';
import finalFrag from '../shaders/final.frag?raw';
import type { StyleParams, QualitySettings } from '../types';
import { EDGE_ID } from '../../world/mesh/attributes';
import { luminance, type LinearRGB } from '../../core/color';
import type { Daylight } from '../../time/daylight';
import type { NightLight } from '../../world/types';
import { NIGHT, flicker } from '../night/nightLights';

/**
 * Pixels final.frag marches at most to measure how far an object runs on behind its outline. The
 * high tier asks for this span at 3291 render rows (a window 2194 px tall at its pixel ratio of
 * 1.5); taller renders keep this span.
 */
export const OUTLINE_SPAN_LIMIT_PX = 32;

/** final.frag lightStrokeColour: below this luminance a face lends no hue worth scaling up. */
export const LIGHT_STROKE_FACE_FLOOR = 1e-4;

/**
 * final.frag lightStrokeColour: the highlight colour with `faceHue` of it replaced by the face's own
 * hue at the highlight colour's luminance.
 */
export function lightStrokeColour(
  highlight: LinearRGB,
  face: readonly [number, number, number],
  faceHue: number
): LinearRGB {
  const gain = luminance(highlight) / Math.max(luminance(face), LIGHT_STROKE_FACE_FLOOR);
  return [0, 1, 2].map(
    (i) => highlight[i]! + (face[i]! * gain - highlight[i]!) * faceHue
  ) as unknown as LinearRGB;
}

export interface FinalPassUniforms {
  uDayExposure: IUniform<number>;
  uDayTint: IUniform<Vector3>;
  tColor: IUniform<Texture | null>;
  tGeom: IUniform<Texture | null>;
  uResolution: IUniform<Vector2>;
  uLineColor: IUniform<Vector3>;
  uLineOpacity: IUniform<number>;
  uLineWidthPx: IUniform<number>;
  uWobblePx: IUniform<number>;
  uDepthThreshold: IUniform<number>;
  uNormalThreshold: IUniform<number>;
  uOutlineSpanPx: IUniform<number>;
  uGrainStrength: IUniform<number>;
  uGrainCellPx: IUniform<number>;
  uLineFade: IUniform<number>;
  uFogDensity: IUniform<number>;
  uSkyEdgeId: IUniform<number>;
  uWaterEdgeId: IUniform<number>;
  uFirstObjectEdgeId: IUniform<number>;
  uFirstVesselEdgeId: IUniform<number>;
  uHighlightColor: IUniform<Vector3>;
  uHighlightOpacity: IUniform<number>;
  uHighlightLitCos: IUniform<number>;
  uHighlightFaceHue: IUniform<number>;
  uSunDirection: IUniform<Vector3>;
  uPoolCount: IUniform<number>;
  uPoolPlace: IUniform<Vector4[]>;
  uPoolColour: IUniform<Vector3[]>;
  uPoolEye: IUniform<Vector3>;
  uPoolRays: IUniform<Matrix4>;
}

export function createFinalPassMaterial(
  style: StyleParams,
  quality: QualitySettings
): ShaderMaterial {
  const lineColor = style.linework.color;
  const { highlight } = style.linework;
  const sun = style.light.sunDirection;

  const uniforms: FinalPassUniforms = {
    uDayExposure: { value: 1 },
    uDayTint: { value: new Vector3(1, 1, 1) },
    tColor: { value: null },
    tGeom: { value: null },
    uResolution: { value: new Vector2(1, 1) },
    uLineColor: { value: new Vector3(lineColor[0], lineColor[1], lineColor[2]) },
    uLineOpacity: { value: style.linework.opacity },
    uLineWidthPx: { value: quality.lineWidthPxAt1080 },
    uWobblePx: { value: style.linework.wobblePx },
    uDepthThreshold: { value: style.linework.depthThreshold },
    uNormalThreshold: { value: style.linework.normalThreshold },
    uOutlineSpanPx: {
      value: outlineSpanPx(style.linework.outlineSpanLineWidths, quality.lineWidthPxAt1080),
    },
    uGrainStrength: { value: quality.grain ? style.canvas.grainStrength : 0 },
    uGrainCellPx: { value: 1.5 },
    uLineFade: { value: style.linework.atmosphereFade },
    uFogDensity: { value: style.atmosphere.density },
    uSkyEdgeId: { value: EDGE_ID.sky },
    uWaterEdgeId: { value: EDGE_ID.water },
    uFirstObjectEdgeId: { value: EDGE_ID.firstObject },
    uFirstVesselEdgeId: { value: EDGE_ID.firstVessel },
    uHighlightColor: {
      value: new Vector3(highlight.color[0], highlight.color[1], highlight.color[2]),
    },
    uHighlightOpacity: { value: highlight.opacity },
    uHighlightLitCos: { value: highlight.litCos },
    uHighlightFaceHue: { value: highlight.faceHue },
    uSunDirection: { value: new Vector3(sun[0], sun[1], sun[2]) },
    uPoolCount: { value: 0 },
    uPoolPlace: { value: Array.from({ length: NIGHT.pools }, () => new Vector4(0, 0, 0, 1)) },
    uPoolColour: { value: Array.from({ length: NIGHT.pools }, () => new Vector3()) },
    uPoolEye: { value: new Vector3() },
    uPoolRays: { value: new Matrix4() },
  };

  const material = new ShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: fullscreenVert,
    fragmentShader: finalFrag,
    defines: { NIGHT_POOLS: NIGHT.pools },
    uniforms: uniforms as unknown as Record<string, IUniform>,
    depthWrite: false,
    depthTest: false,
  });
  const base: FinalPassStyle = {
    grainStrength: style.canvas.grainStrength,
    wobblePxAt1080: style.linework.wobblePx,
    outlineSpanLineWidths: style.linework.outlineSpanLineWidths,
  };
  material.userData['finalPassStyle'] = base;
  return material;
}

interface FinalPassStyle {
  readonly grainStrength: number;
  readonly wobblePxAt1080: number;
  readonly outlineSpanLineWidths: number;
}

export function updateFinalPassDaylight(material: ShaderMaterial, light: Daylight): void {
  const uniforms = material.uniforms as unknown as FinalPassUniforms;
  uniforms.uDayExposure.value = light.exposure;
  uniforms.uDayTint.value.set(...light.tint);
  uniforms.uSunDirection.value.set(...light.sun).normalize();
}

/**
 * The flames whose light the final pass lays on what stands around them, nearest first, as the
 * camera now sees them; `night` is how far the day has gone (time/daylight.ts), and none are drawn
 * in the painter view, whose projection moves every surface away from where its flame stands.
 */
export function updateFinalPassPools(
  material: ShaderMaterial,
  lights: readonly NightLight[],
  camera: PerspectiveCamera,
  night: number,
  timeSeconds: number
): void {
  const uniforms = material.uniforms as unknown as FinalPassUniforms;
  const count = night > 0 ? Math.min(lights.length, NIGHT.pools) : 0;
  uniforms.uPoolCount.value = count;
  if (!count) return;
  for (let i = 0; i < count; i++) {
    const light = lights[i]!,
      strength = night * flicker(light.phase, timeSeconds);
    uniforms.uPoolPlace.value[i]!.set(...light.position, light.reach);
    uniforms.uPoolColour.value[i]!.set(...light.colour).multiplyScalar(strength);
  }
  uniforms.uPoolEye.value.copy(camera.position);
  uniforms.uPoolRays.value.multiplyMatrices(camera.matrixWorld, camera.projectionMatrixInverse);
}

function outlineSpanPx(lineWidths: number, lineWidthPx: number): number {
  return Math.min(lineWidths * lineWidthPx, OUTLINE_SPAN_LIMIT_PX);
}

export function updateFinalPassQuality(
  material: ShaderMaterial,
  quality: QualitySettings,
  renderHeight: number
): void {
  const uniforms = material.uniforms as unknown as FinalPassUniforms;
  const base = material.userData['finalPassStyle'] as FinalPassStyle;

  const lineScale = renderHeight / 1080;
  uniforms.uLineWidthPx.value = quality.lineWidthPxAt1080 * lineScale;
  uniforms.uOutlineSpanPx.value = outlineSpanPx(
    base.outlineSpanLineWidths,
    uniforms.uLineWidthPx.value
  );
  uniforms.uWobblePx.value = base.wobblePxAt1080 * lineScale;
  uniforms.uGrainStrength.value = quality.grain ? base.grainStrength : 0;
}
